import { useState } from "react";
import { Card } from "primereact/card";
import { activeCurrencies } from "utils";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormField from "components/v2/FormField";
import useEditBook from "api/BookResource/useEditBook";
import RichTextEditor from "components/RichTextEditor";
import { IoIosArrowForward } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import DeleteBook from "./DeleteBook";
import useSetPickOfTheWeek from "api/BookResource/useSetPickOfTheWeek";
import useSetRecommended from "api/BookResource/useSetRecommended";
import useSetSpotlightedBooks from "api/BookResource/useSetSpotlightedBooks";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import Dialog from "components/Overlays/ResponsiveDialog";
import { Tooltip } from "primereact/tooltip";
import { CgArrowLongRight } from "react-icons/cg";
import { BookFormat } from "utils/server-constants/bookFormats";
import { FiBookOpen, FiTag, FiAward, FiStar, FiZap, FiUploadCloud } from "react-icons/fi";
import { BiBookAlt } from "react-icons/bi";
import { BsCheckAll } from "react-icons/bs";
import { BookFilesUploadModal } from "components/BookUpload";
import { getSubmitButtonLabel } from "api/BookResource/bookFileUpload";

const BookCard = ({
	bookId,
	title,
	author,
	price,
	zwlPrice,
	isbn,
	bookCover,
	publisher,
	isActive,
	isFree,
	sellable,
	onSubscription,
	category,
	genres,
	description,
	className,
	hasFile,
	numberOfCopiesPurchased,
	copiesAssignable,
	copiesSubscribed,
	subscriptionSeatsAssigned,
	collections,
	categories,
	format,
	stockLevel,
	physicalUsdPrice,
	physicalZwlPrice,
	pick_of_week,
	recommended,
	is_spotlighted,
	isSelected,
	onSelect,
}) => {
	const [visible, setVisible] = useState(false);
	const [showFilesModal, setShowFilesModal] = useState(false);

	// const { data: categories, isLoading: isCategoriesLoading } =
	// 	useFetchCategories();

	// const { data: collections, isLoading: isGenresLoading } = useGenres();

	const hasRoles = useAuthStore((state) => state.hasRoles);

	// const { currency } = useCurrencyStore();

	// function formattedPrice() {
	// 	if (isFree) {
	// 		return "";
	// 	} else if (currency === activeCurrencies.USD.code) {
	// 		return `$${price}`;
	// 	} else {
	// 		return `$${zwlPrice}`;
	// 	}
	// }

	const initialValues = {
		title: title || "",
		isbn: isbn || "",
		author: author || "",
		price: price || "",
		zwl_price: zwlPrice || "",
		description: description || "",
		genres: genres?.id || "",
		category_id: category?.id || "",
		subscription: Boolean(onSubscription),
		sellable: Boolean(sellable),
		// is_active: Boolean(isActive),
		isFree: Boolean(isFree),
		format: format || "",
		stock_level: stockLevel || "",
		physical_usd_price: physicalUsdPrice || "",
		physical_zwl_price: physicalZwlPrice || "",
	};

	const validate = Yup.object().shape({
		title: Yup.string().required("Book title is required"),
		author: Yup.string().required("Author is required"),
		price: Yup.number().when("isFree", {
			is: true,
			then: Yup.number().notRequired(),
			otherwise: Yup.number()
				.required("Price is required")
				.moreThan(0, "Price should be greater than 0"),
		}),

		description: Yup.mixed(),
		category_id: Yup.number().required("Book category is required"),
		genres: Yup.string().required("Book genre is required"),
		// apply validation if use role is SuperAdmin or Admin
		format: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.string().required("Book format is required")
			: Yup.mixed().notRequired(),

		stock_level: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => ["physical", "both"].includes(format),
					then: Yup.number()
						.typeError("Stock level mus be a number")
						.required("Stock level is required"),
					otherwise: Yup.mixed().notRequired(),
				})
			: Yup.mixed().notRequired(),

		physical_usd_price: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => [BookFormat.Physical, BookFormat.Both].includes(format),
					then: Yup.number()
						.typeError("Price must be a number")
						.required("Set price of the physical book"),
					otherwise: Yup.mixed().notRequired(),
				})
			: Yup.mixed().notRequired(),

		physical_zwl_price: hasRoles([Role.SuperAdmin, Role.Admin])
			? Yup.mixed().when("format", {
					is: (format) => [BookFormat.Physical, BookFormat.Both].includes(format),
					then: Yup.number()
						.typeError("Price must be a number")
						.required("Set price of the physical book"),
					otherwise: Yup.mixed().notRequired(),
				})
			: Yup.mixed().notRequired(),
	});

	const { mutateAsync, isLoading } = useEditBook(bookId, () => setVisible(false));

	const handleSubmit = async (data) => {
		await mutateAsync({
			...data,
			is_active: data.is_active ? 1 : 0,
			subscription: data.subscription ? 1 : 0,
			sellable: data.sellable ? 1 : 0,
			isFree: data.isFree ? 1 : 0,
		});
	};

	const handleOpenUploadFilesFromEdit = () => {
		setVisible(false);
		setTimeout(() => setShowFilesModal(true), 200);
	};

	const { mutateAsync: mutatePickOfTheWeek, isLoading: isSettingPickOfTheWeek } =
		useSetPickOfTheWeek(bookId);

	const setAsPickofTheWeek = async () => {
		await mutatePickOfTheWeek();
	};

	const { mutateAsync: mutateSetRecommended, isLoading: isSettingRecommended } =
		useSetRecommended(bookId);

	const setAsRecommended = async () => {
		await mutateSetRecommended();
	};

	const { mutateAsync: mutateSetSpotlight, isLoading: isSettingSpotlight } =
		useSetSpotlightedBooks();

	const toggleSpotlight = async () => {
		try {
			await mutateSetSpotlight({ bookId });
		} catch (error) {
			console.error("Failed to toggle spotlight:", error);
		}
	};

	return (
		<>
			<Card
				className={`font-sans w-full shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl border ${isSelected ? "border-akgreener ring-1 ring-akgreener" : "border-gray-100"} overflow-hidden group ${className}`}
			>
				<div className="flex flex-col sm:flex-row w-full gap-6 relative p-2">
					{/* Selection Overlay */}
					{onSelect && (
						<div
							onClick={onSelect}
							className={`absolute top-4 left-4 z-20 w-6 h-6 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all ${
								isSelected
									? "bg-akgreener border-akgreener shadow-md scale-110"
									: "bg-white bg-opacity-80 border-gray-300 hover:border-akgreen backdrop-blur-sm opacity-0 group-hover:opacity-100"
							}`}
						>
							{isSelected && <BsCheckAll size={18} className="text-white" />}
						</div>
					)}

					{/* Book cover section */}
					<div className="relative flex-shrink-0 flex justify-center">
						<div
							className="bg-gray-50 flex justify-center items-center rounded-xl overflow-hidden shadow-sm border border-gray-100"
							style={{ height: "240px", width: "165px" }}
						>
							{bookCover ? (
								<img
									src={bookCover}
									alt={title}
									className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
								/>
							) : (
								<BiBookAlt size={48} className="text-gray-300" />
							)}
						</div>

						{isFree && (
							<div className="absolute top-2 left-2 z-10">
								<span className="text-xs uppercase tracking-wider font-bold py-1 px-2.5 rounded-lg bg-akgreener text-white shadow-sm flex items-center gap-1">
									<div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
									Free
								</span>
							</div>
						)}
					</div>

					{/* Book details section */}
					<div className="flex flex-col flex-1 py-1 pr-2">
						<div className="space-y-1.5">
							<div className="flex justify-between items-start gap-2">
								<h3 className="text-lg font-bold text-gray-900 line-clamp-2 leading-snug">
									{title}
								</h3>
							</div>

							<div className="flex flex-col gap-0.5">
								<p className="text-gray-600 text-sm font-medium">
									By <span className="text-gray-900 font-semibold">{author}</span>
								</p>
								<div className="flex items-center gap-2 text-xs text-gray-400">
									<span className="truncate">{publisher}</span>
									{isbn && (
										<>
											<span className="w-1 h-1 rounded-full bg-gray-300" />
											<span>{isbn}</span>
										</>
									)}
								</div>
							</div>
						</div>

						{/* Status badges */}
						{hasRoles([Role.SeniorPublisher, Role.Publisher, Role.SuperAdmin, Role.Admin]) && (
							<div className="flex flex-wrap gap-2 mt-3">
								<span
									className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide transition-colors ${
										hasFile
											? "bg-akgreen bg-opacity-10 text-akgreener border border-akgreen border-opacity-20"
											: "bg-red-50 text-red-700 border border-red-100"
									}`}
								>
									<FiBookOpen size={13} className={hasFile ? "text-akgreener" : "text-red-600"} />
									{hasFile ? "PDF" : "MISSING FILE"}
								</span>
								<span
									className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide transition-colors ${
										isActive
											? "bg-akgreenest bg-opacity-10 text-akgreenest border border-akgreenest border-opacity-20"
											: "bg-amber-50 text-amber-700 border border-amber-100"
									}`}
								>
									<FiTag size={13} className={isActive ? "text-akgreenest" : "text-amber-600"} />
									{isActive ? "ACTIVE" : "INACTIVE"}
								</span>
							</div>
						)}

						{/* Institution/Educator specific */}
						{hasRoles([Role.Institution, Role.Educator]) && (
							<div className="mt-4 grid grid-cols-3 gap-2">
								<div className="bg-gray-50 p-2 rounded-lg border border-gray-100 text-center">
									<p className="text-xs text-gray-400 uppercase font-bold">Seats</p>
									<p className="text-sm font-bold text-gray-900">{copiesSubscribed || 0}</p>
								</div>
								<div className="bg-gray-50 p-2 rounded-lg border border-gray-100 text-center">
									<p className="text-xs text-gray-400 uppercase font-bold">Assigned</p>
									<p className="text-sm font-bold text-orangelib">
										{subscriptionSeatsAssigned || 0}
									</p>
								</div>
								<div className="bg-gray-50 p-2 rounded-lg border border-gray-100 text-center">
									<p className="text-xs text-gray-400 uppercase font-bold">Available</p>
									<p className="text-sm font-bold text-akgreener">{copiesAssignable || 0}</p>
								</div>
							</div>
						)}

						{/* Bottom Section: Price & Primary Actions */}
						<div className="mt-auto pt-4 flex items-center justify-between gap-4 border-t border-gray-50">
							<div className="flex flex-col">
								{isFree ? (
									<span className="text-akgreener font-bold text-lg">Free</span>
								) : (
									<div className="flex items-baseline gap-1">
										<span className="text-gray-900 font-bold text-lg">${price}</span>
										<span className="text-gray-400 text-xs font-normal">USD</span>
									</div>
								)}
							</div>

							<div className="flex items-center gap-2">
								{hasRoles([Role.SuperAdmin, Role.Admin, Role.SeniorPublisher, Role.Publisher]) && (
									<button
										type="button"
										onClick={() => setVisible(true)}
										className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-akgreenest text-white text-xs font-bold rounded-xl hover:bg-black transition-all shadow-sm active:scale-95"
									>
										Manage
										<CgArrowLongRight size={16} />
									</button>
								)}
							</div>
						</div>

						{/* Quick Admin Actions Floating */}
						{hasRoles([Role.SuperAdmin, Role.Admin]) && (
							<div className="absolute top-2 right-2 flex flex-col gap-2">
								<Tooltip target=".admin-tool-btn" position="left" />

								<button
									type="button"
									onClick={() => setAsPickofTheWeek()}
									className={`admin-tool-btn w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
										pick_of_week
											? "bg-orangelib bg-opacity-10 text-orangelib shadow-inner"
											: "bg-white text-gray-400 hover:text-orangelib shadow-sm border border-gray-100"
									}`}
									data-pr-tooltip={
										pick_of_week ? "Featured: Pick of the Week" : "Feature as Pick of the Week"
									}
								>
									{isSettingPickOfTheWeek ? (
										<AiOutlineLoading3Quarters size={14} className="animate-spin" />
									) : (
										<FiAward size={16} fill={pick_of_week ? "currentColor" : "none"} />
									)}
								</button>

								<button
									type="button"
									onClick={() => setAsRecommended()}
									className={`admin-tool-btn w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
										recommended
											? "bg-akgreen bg-opacity-10 text-akgreener shadow-inner"
											: "bg-white text-gray-400 hover:text-akgreen shadow-sm border border-gray-100"
									}`}
									data-pr-tooltip={recommended ? "Recommended" : "Recommend this book"}
								>
									{isSettingRecommended ? (
										<AiOutlineLoading3Quarters size={14} className="animate-spin" />
									) : (
										<FiStar size={16} fill={recommended ? "currentColor" : "none"} />
									)}
								</button>

								<button
									type="button"
									onClick={() => toggleSpotlight()}
									className={`admin-tool-btn w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
										is_spotlighted
											? "bg-yellow-100 text-yellow-600 shadow-inner"
											: "bg-white text-gray-400 hover:text-yellow-500 shadow-sm border border-gray-100"
									}`}
									data-pr-tooltip={is_spotlighted ? "Spotlighted" : "Add to Spotlight"}
								>
									{isSettingSpotlight ? (
										<AiOutlineLoading3Quarters size={14} className="animate-spin" />
									) : (
										<FiZap size={16} fill={is_spotlighted ? "currentColor" : "none"} />
									)}
								</button>

								<div className="admin-tool-btn opacity-0 hover:opacity-100 transition-opacity">
									<DeleteBook elementId={`delete-${bookId}`} bookId={bookId} />
								</div>
							</div>
						)}
					</div>
				</div>
			</Card>

			<Dialog
				visible={visible}
				style={{ width: "min(720px, 95vw)" }}
				onHide={() => setVisible(false)}
				focusOnShow={false}
				header={
					<div>
						<p className="text-lg font-semibold text-gray-900">Edit book</p>
						<p className="text-sm font-normal text-gray-500 mt-0.5">{title}</p>
					</div>
				}
				position="center"
				draggable={false}
				modal
				className="rounded-xl"
				contentClassName="rounded-b-xl"
			>
				<Formik
					key={bookId}
					initialValues={initialValues}
					validationSchema={validate}
					onSubmit={handleSubmit}
				>
					{({ setFieldValue, values, errors, touched }) => {
						const hasPhysical = [BookFormat.Physical, BookFormat.Both].includes(
							values?.format || "",
						);

						return (
							<Form className="space-y-6 pt-2">
								<div className="flex gap-3">
									<div className="w-1/2">
										<FormField type="text" name="title" label="Title *" />
									</div>
									<div className="w-1/2">
										<FormField type="text" name="isbn" label="ISBN Number" />
									</div>
								</div>

								<div className="w-full">
									<FormField type="text" name="author" label="Author Name *" />
								</div>

								{hasRoles([Role.SuperAdmin, Role.Admin]) && (
									<div className={hasPhysical ? "flex flex-row gap-2" : "flex flex-col gap-6"}>
										<div className={hasPhysical ? "w-1/2" : "w-full"}>
											<FormField
												type="select"
												name="format"
												label="Book format"
												options={[
													{
														label: "Digital copies (for online reading)",
														value: "digital",
													},
													{
														label: "Physical copies",
														value: "physical",
													},
													{
														label: "Both physical and digital copies",
														value: "both",
													},
												].map((option) => ({
													label: option.label,
													value: option.value,
												}))}
											/>
										</div>
										<div className={hasPhysical ? "w-1/2" : "w-full hidden"}>
											<FormField
												type="number"
												min="0"
												step="0.01"
												name="stock_level"
												label="Number of physical copies available"
											/>
										</div>
									</div>
								)}

								<div className="flex gap-2">
									<div className="w-1/2">
										<FormField
											type="number"
											min="0"
											step="0.01"
											name="price"
											label={`Price (${activeCurrencies.USD.label})`}
										/>
									</div>
									<div className="w-1/2">
										<FormField
											type="number"
											min="0"
											step="0.01"
											name="zwl_price"
											label={`Price (${activeCurrencies.ZWL.label})`}
										/>
									</div>
								</div>

								{hasRoles([Role.SuperAdmin, Role.Admin]) && (
									<div className={` ${hasPhysical ? "flex gap-2" : "hidden"}`}>
										<div className="w-1/2">
											<FormField
												type="number"
												min="0"
												step="0.01"
												name="physical_usd_price"
												label={`Physical book price (${activeCurrencies.USD.label})`}
											/>
										</div>
										<div className="w-1/2">
											<FormField
												type="number"
												min="0"
												step="0.01"
												name="physical_zwl_price"
												label={`Physical book price (${activeCurrencies.ZWL.label})`}
											/>
										</div>
									</div>
								)}

								<div className="flex gap-2">
									<div className="w-1/2">
										<FormField
											type="select"
											name="category_id"
											label="Book category"
											options={categories?.data?.map((category) => ({
												label: category.name,
												value: category.id,
											}))}
										/>
									</div>
									<div className="w-1/2">
										<FormField
											type="select"
											name="genres"
											label="Book Genre"
											options={collections?.data?.data?.map((genre) => ({
												label: genre.name,
												value: genre.id,
											}))}
										/>
									</div>
								</div>

								<div className="space-y-2">
									<p className="text-gray-500">Book summary</p>
									<RichTextEditor
										resetKey={bookId}
										value={description || ""}
										onTextChange={(e) => setFieldValue("description", e.htmlValue, false)}
										error={!!(touched.description && errors.description) && errors.description}
									/>
								</div>

								<div className="space-y-2">
									<FormField
										type="switcher"
										name="subscription"
										label="Do you want people to subscribe to this book?"
									/>

									<FormField type="switcher" label="Can people buy this book?" name="sellable" />

									<FormField type="switcher" label="Make the book free?" name="isFree" />
								</div>

								<div className="border-t border-gray-300 pt-4 mt-2 flex items-center justify-between gap-3">
									<button
										type="button"
										onClick={handleOpenUploadFilesFromEdit}
										className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-akgreener text-akgreener text-sm font-semibold hover:bg-akgreen bg-opacity-5 transition-colors"
									>
										<FiUploadCloud size={16} />
										Upload files
									</button>
									<button
										type="submit"
										disabled={isLoading}
										className="px-6 py-2.5 rounded-full bg-akgreenest hover:bg-black text-white font-semibold flex gap-2 items-center justify-center transition-all active:scale-95 disabled:opacity-70 text-sm"
									>
										{getSubmitButtonLabel({ isLoading, mode: "edit" })}
										{isLoading && <AiOutlineLoading3Quarters size={18} className="animate-spin" />}
										{!isLoading && <IoIosArrowForward size={18} />}
									</button>
								</div>
							</Form>
						);
					}}
				</Formik>
			</Dialog>

			<BookFilesUploadModal
				visible={showFilesModal}
				onHide={() => setShowFilesModal(false)}
				bookId={bookId}
				bookTitle={title}
				hasExistingCover={Boolean(bookCover)}
				hasExistingFile={Boolean(hasFile)}
				allowSkip
			/>
		</>
	);
};

export default BookCard;
